import { useEffect, useState, type CSSProperties } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  TextFilter,
  Toggle,
  TreeView,
} from '@cloudscape-design/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { colorBackgroundItemSelected, colorBorderDividerSecondary, colorTextBodySecondary } from '@cloudscape-design/design-tokens';
import type { components } from '../../api/schema';
import {
  getDashboardHierarchy,
  updateDashboardNotification,
} from '../../common/api/distributorDashboard';
import {
  getDistributorHierarchy,
  updateDistributorNotification,
} from '../../common/api/distributors';
import './distributor-hierarchy.css';

type Hierarchy = components['schemas']['DistributorHierarchyRead'];
type HierarchyItem = components['schemas']['DistributorHierarchyItem'];

type Props = {
  rootUuid?: string;
  onViewScorecards?: (item: HierarchyItem) => void;
};

function name(item: HierarchyItem): string {
  return `${item.first_name} ${item.last_name}`.trim() || item.company || item.path;
}

function NotificationPreference({
  item,
  root,
  internal,
}: {
  item: HierarchyItem;
  root: HierarchyItem;
  internal: boolean;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (enabled: boolean) => internal
      ? updateDistributorNotification(root.uuid, item.uuid, enabled)
      : updateDashboardNotification(item.uuid, enabled),
    onSuccess: (result) => {
      queryClient.setQueriesData<Hierarchy>(
        { queryKey: ['distributor-hierarchy'] },
        (previous) => previous?.root_uuid === root.uuid
          ? {
              ...previous,
              items: previous.items.map((entry) => entry.uuid === item.uuid
                ? { ...entry, notifications_enabled: result.enabled }
                : entry),
            }
          : previous,
      );
    },
  });

  return (
    <SpaceBetween size="xxs">
      <Toggle
        checked={mutation.isPending ? mutation.variables : item.notifications_enabled === true}
        disabled={mutation.isPending}
        onChange={({ detail }) => mutation.mutate(detail.checked)}
        ariaLabel={`Email ${internal ? name(root) : 'me'} for ${name(item)}`}
      >
        {(mutation.isPending ? mutation.variables : item.notifications_enabled) ? 'On' : 'Off'}
      </Toggle>
      {mutation.isPending && <StatusIndicator type="loading">Saving</StatusIndicator>}
      {mutation.isError && (
        <div role="alert">
          <SpaceBetween size="xxs">
            <StatusIndicator type="error">Could not save</StatusIndicator>
            <Button variant="inline-link" onClick={() => mutation.mutate(mutation.variables)}>
              Retry
            </Button>
          </SpaceBetween>
        </div>
      )}
    </SpaceBetween>
  );
}

export function DistributorHierarchy({ rootUuid, onViewScorecards }: Props) {
  const internal = !!rootUuid;
  const [expanded, setExpanded] = useState<Set<string> | null>(null);
  const [search, setSearch] = useState('');
  const [compact, setCompact] = useState(() => window.innerWidth < 720);
  useEffect(() => {
    const update = () => setCompact(window.innerWidth < 720);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const query = useQuery({
    queryKey: ['distributor-hierarchy', rootUuid ?? 'dashboard'],
    queryFn: () => rootUuid ? getDistributorHierarchy(rootUuid) : getDashboardHierarchy(),
  });
  const items = query.data?.items ?? [];
  const root = items.find((item) => item.uuid === query.data?.root_uuid);
  const byId = new Map(items.map((item) => [item.uuid, item]));
  const children = new Map<string, HierarchyItem[]>();
  for (const item of items) {
    if (item.parent_uuid) {
      const siblings = children.get(item.parent_uuid) ?? [];
      siblings.push(item);
      children.set(item.parent_uuid, siblings);
    }
  }
  // Derive depth and subtree sizes iteratively, without a frontend depth limit.
  const ordered = root ? [root] : [];
  const depths = new Map(root ? [[root.uuid, 0]] : []);
  for (let index = 0; index < ordered.length; index++) {
    const item = ordered[index];
    const safeChildren = (children.get(item.uuid) ?? []).filter((child) => !depths.has(child.uuid));
    children.set(item.uuid, safeChildren);
    for (const child of safeChildren) {
      depths.set(child.uuid, (depths.get(item.uuid) ?? 0) + 1);
      ordered.push(child);
    }
  }
  const counts = new Map<string, number>();
  for (const item of [...ordered].reverse()) {
    if (item.parent_uuid && item.uuid !== root?.uuid) {
      counts.set(item.parent_uuid, (counts.get(item.parent_uuid) ?? 0) + 1 + (counts.get(item.uuid) ?? 0));
    }
  }
  for (const siblings of children.values()) {
    siblings.sort((a, b) => (counts.get(b.uuid) ?? 0) - (counts.get(a.uuid) ?? 0)
      || name(a).localeCompare(name(b)));
  }
  const term = search.trim().toLowerCase();
  const included = new Set<string>();
  if (term) {
    // A match keeps its branch and every ancestor, rather than flattening search results.
    for (const item of ordered) {
      if (included.has(item.parent_uuid ?? '')
        || `${name(item)} ${item.company ?? ''} ${item.path}`.toLowerCase().includes(term)) {
        included.add(item.uuid);
      }
    }
    for (const item of [...ordered].reverse()) {
      if (included.has(item.uuid) && item.parent_uuid) included.add(item.parent_uuid);
    }
  }
  const branchIds = ordered.filter((item) => (children.get(item.uuid)?.length ?? 0) > 0).map((item) => item.uuid);
  const expandedIds = [...(expanded ?? new Set(term ? branchIds : root ? [
    root.uuid,
    ...(children.get(root.uuid)?.slice(0, 1).map((item) => item.uuid) ?? []),
  ] : []))];
  const maxDepth = Math.max(1, ...depths.values());

  return (
    <Container header={
      <Header
        variant="h2"
        counter={root ? `(${Math.max(0, items.length - 1).toLocaleString()})` : undefined}
        description={root ? `Scorecard emails to ${internal ? name(root) : 'you'}` : undefined}
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button disabled={!branchIds.length} onClick={() => setExpanded(new Set(branchIds))}>Expand all</Button>
            <Button disabled={!branchIds.length} onClick={() => setExpanded(new Set())}>Collapse all</Button>
          </SpaceBetween>
        }
      >
        Downline
      </Header>
    }>
      <SpaceBetween size="m">
        <TextFilter
          filteringText={search}
          filteringAriaLabel="Find a distributor"
          filteringPlaceholder="Find a distributor"
          onChange={({ detail }) => {
            setSearch(detail.filteringText);
            setExpanded(null);
          }}
        />
        {query.isError && (
          <div role="alert">
            <Alert type="error" action={<Button onClick={() => query.refetch()}>Retry</Button>}>
              Could not load downline. Try again.
            </Alert>
          </div>
        )}
        {query.isPending ? <Spinner /> : root && (!term || included.size > 0) ? (
          <div
            className="distributor-hierarchy"
            style={{
              '--hierarchy-indent': `${Math.min(compact ? 16 : 28, (compact ? 64 : 224) / maxDepth)}px`,
              '--hierarchy-border': colorBorderDividerSecondary,
              '--hierarchy-root-background': colorBackgroundItemSelected,
              '--hierarchy-secondary': colorTextBodySecondary,
            } as CSSProperties}
          >
            <TreeView
              items={[root]}
              getItemId={(item) => item.uuid}
              getItemChildren={(item) => (children.get(item.uuid) ?? []).filter((child) => !term || included.has(child.uuid))}
              expandedItems={expandedIds}
              onItemToggle={({ detail }) => {
                const next = new Set(expandedIds);
                if (detail.expanded) next.add(detail.id);
                else next.delete(detail.id);
                setExpanded(next);
              }}
              connectorLines="vertical"
              ariaLabel="Distributor hierarchy"
              i18nStrings={{ expandButtonLabel: () => 'Expand', collapseButtonLabel: () => 'Collapse' }}
              renderItem={(item) => ({
                announcementLabel: name(item),
                content: (
                  <div className={`downline-node${item.uuid === root.uuid ? ' downline-root' : ''}`}>
                    <div className="downline-identity">
                      <div className="downline-name">
                        <span className={counts.has(item.uuid) || item.uuid === root.uuid ? 'downline-parent' : ''}>{name(item)}</span>
                        {item.uuid !== root.uuid && counts.has(item.uuid) && <span className="downline-count">{counts.get(item.uuid)} downline</span>}
                      </div>
                      {item.company && item.company !== name(item) && <Box color="text-body-secondary">{item.company}</Box>}
                      {(depths.get(item.uuid) ?? 0) > 4 && item.parent_uuid && <Box color="text-body-secondary">Under {name(byId.get(item.parent_uuid)!)}</Box>}
                      {item.disabled && <StatusIndicator type="stopped">Disabled</StatusIndicator>}
                    </div>
                    <div className="downline-actions">
                      <Button
                        variant="inline-link"
                        wrapText
                        ariaLabel={`View scorecards for ${name(item)}`}
                        href={internal ? `/dashboard/distributors/${item.uuid}` : undefined}
                        onClick={internal ? undefined : () => onViewScorecards?.(item)}
                      >
                        {item.scorecard_count.toLocaleString()} {item.scorecard_count === 1 ? 'scorecard' : 'scorecards'}
                      </Button>
                      <div className="downline-notification">
                        {item.uuid !== root.uuid && <NotificationPreference key={`${root.uuid}:${item.uuid}`} item={item} root={root} internal={internal} />}
                      </div>
                    </div>
                  </div>
                ),
              })}
            />
          </div>
        ) : !query.isError && <Box textAlign="center" padding="l">{term ? 'No matching distributors' : 'No downline distributors'}</Box>}
      </SpaceBetween>
    </Container>
  );
}
